import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Trophy, Flame, Share2, Check, Clock, IceCream, Sparkles, Link as LinkIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { SUNDAE_GAME_LABELS } from '../../services/sundaeApi';
import SundaeLeaderboard from './SundaeLeaderboard';
import { useLanguage } from '../../contexts/LanguageContext';

const fmtTime = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Final summary card surfaced after the user finishes all 5 sundae games.
 * Shows per-game line items, bonuses, total, elapsed time, share, and a
 * "Group Mode" deep link the user can paste into a chat for their group
 * to play the same date's sundae and see a date-scoped leaderboard.
 */
const SundaeShareCard = ({ language = 'en', state, onClose }) => {
  const { t } = useLanguage();
  const [copiedShare, setCopiedShare] = useState(false);
  const [copiedGroup, setCopiedGroup] = useState(false);
  const [showLb, setShowLb] = useState(true);

  if (!state) return null;
  const total = state.games?.length || 5;
  const correct = state.games?.filter((g) => g.completed).length || 0;
  const elapsed = state.total_elapsed_ms || 0;
  const speed = state.bonus_speed > 0;

  const groupUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/community?tab=games&sundae=${state.date}`
      : '';

  const buildShareText = () => {
    const lines = state.games.map((g) => {
      const label = SUNDAE_GAME_LABELS[g.game]?.[language] || g.game;
      return `  • ${label}: +${g.score}`;
    });
    return [
      t('games.sundae.shareHeader', { date: state.date }),
      t('games.sundae.shareTotal', { score: state.sundae_score, time: fmtTime(elapsed) }),
      ...lines,
      state.bonus_all_5 ? t('games.sundae.shareAll5', { n: state.bonus_all_5 }) : '',
      speed ? t('games.sundae.shareSpeed', { n: state.bonus_speed }) : '',
      t('games.sundae.sharePlay', { url: groupUrl }),
    ].filter(Boolean).join('\n');
  };

  const onShare = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('games.sundae.myShareTitle'),
          text,
          url: groupUrl,
        });
        return;
      } catch {
        /* user cancelled — fall through to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedShare(true);
      toast.success(t('games.sundae.copied'));
      setTimeout(() => setCopiedShare(false), 2000);
    } catch {
      toast.error(t('games.sundae.couldNotShare'));
    }
  };

  const onCopyGroup = async () => {
    try {
      await navigator.clipboard.writeText(groupUrl);
      setCopiedGroup(true);
      toast.success(t('games.sundae.groupLinkCopied'));
      setTimeout(() => setCopiedGroup(false), 2000);
    } catch {
      toast.error(t('games.sundae.couldNotCopy'));
    }
  };

  return (
    <div className="space-y-5" data-testid="sundae-share-card-wrap">
      <Card
        className="bg-gradient-to-br from-purple-700 via-fuchsia-600 to-pink-600 text-white border-0 shadow-2xl"
        data-testid="sundae-share-card"
      >
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <IceCream className="w-5 h-5" />
                <p className="text-[10px] uppercase tracking-widest opacity-80">
                  {t('games.sundae.devotionalSundae')}
                </p>
              </div>
              <p className="text-sm opacity-80 italic">{state.date}</p>
            </div>
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-300 text-purple-900 text-[10px] font-bold uppercase tracking-wide">
              <Trophy className="w-3 h-3" />
              {t('games.sundae.done')}
            </div>
          </div>

          <div className="flex items-baseline gap-2">
            <span
              className="text-5xl font-black tabular-nums"
              data-testid="sundae-final-score"
            >
              +{state.sundae_score}
            </span>
            <span className="text-sm opacity-80">
              {t('games.sundae.points')}
            </span>
          </div>

          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 tabular-nums" data-testid="sundae-elapsed">
              <Clock className="w-3.5 h-3.5" />
              {fmtTime(elapsed)}
            </span>
            <span className="opacity-50">·</span>
            <span>{correct}/{total} {t('games.sundae.games')}</span>
            {state.bonus_all_5 > 0 && (
              <Badge
                className="bg-yellow-300/95 text-purple-900 border-0 text-[10px] font-bold"
                data-testid="sundae-bonus-all5"
              >
                <Sparkles className="w-3 h-3 mr-0.5" />
                +{state.bonus_all_5} all-5
              </Badge>
            )}
            {state.bonus_speed > 0 && (
              <Badge
                className="bg-emerald-300/95 text-emerald-900 border-0 text-[10px] font-bold"
                data-testid="sundae-bonus-speed"
              >
                <Flame className="w-3 h-3 mr-0.5" />
                +{state.bonus_speed} speed
              </Badge>
            )}
          </div>

          {/* Per-game breakdown */}
          <div className="space-y-1.5 pt-3 border-t border-white/20" data-testid="sundae-breakdown">
            {(state.order || state.games.map((g) => g.game)).map((g) => {
              const entry = state.games.find((x) => x.game === g);
              const label = SUNDAE_GAME_LABELS[g]?.[language] || g;
              return (
                <div
                  key={g}
                  className="flex items-center justify-between text-sm"
                  data-testid={`sundae-row-${g}`}
                >
                  <span className="opacity-95">{label}</span>
                  <span className="tabular-nums font-semibold">
                    +{entry?.score ?? 0}
                  </span>
                </div>
              );
            })}
          </div>

          <Button
            onClick={onShare}
            className="w-full bg-white text-purple-700 hover:bg-white/90 font-bold"
            data-testid="sundae-share-btn"
          >
            {copiedShare ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
            {t('games.sundae.shareMy')}
          </Button>

          {/* Group Mode deep link */}
          <div className="rounded-md bg-white/10 border border-white/20 px-3 py-2.5 text-xs space-y-2">
            <div className="flex items-center gap-1.5 font-semibold">
              <LinkIcon className="w-3.5 h-3.5" />
              {t('games.sundae.groupMode')}
            </div>
            <p className="opacity-90">
              {t('games.sundae.groupDesc')}
            </p>
            <Button
              onClick={onCopyGroup}
              size="sm"
              variant="outline"
              className="bg-white/0 border-white/40 text-white hover:bg-white/15 hover:text-white h-7"
              data-testid="sundae-copy-group-btn"
            >
              {copiedGroup ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <LinkIcon className="w-3.5 h-3.5 mr-1.5" />}
              {t('games.sundae.copyGroupLink')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Group leaderboard for THIS sundae's date */}
      {showLb && (
        <SundaeLeaderboard
          language={language}
          date={state.date}
          onCollapse={() => setShowLb(false)}
        />
      )}

      {onClose && (
        <Button
          onClick={onClose}
          variant="outline"
          className="w-full"
          data-testid="sundae-close-btn"
        >
          {t('games.sundae.backToHub')}
        </Button>
      )}
    </div>
  );
};

export default SundaeShareCard;
